/**
 * Adapter Finder - Rearview-first flow only.
 * Custom element: adapter-finder-rearview. All querySelector runs inside root.
 * Self-contained; does not depend on adapter-finder.js.
 */
class AdapterFinderRearview {
  constructor(root) {
    this.root = root;

    this.fitmentUrl = root.getAttribute("data-fitment-url");
    this.adapterDataUrl = root.getAttribute("data-adapter-data-url");
    this.sideviewOptionsUrl = (root.getAttribute("data-sideview-options-url") || "").trim() || null;
    this.klaviyoPublicKey = (root.getAttribute("data-klaviyo-public-key") || "").trim();
    this.klaviyoListId = (root.getAttribute("data-klaviyo-list-id") || "").trim();
    this.currentProductHandle = root.getAttribute("data-current-product-handle");

    this.yearEl = root.querySelector("[data-af-year]");
    this.makeEl = root.querySelector("[data-af-make]");
    this.modelEl = root.querySelector("[data-af-model]");
    this.modelWrapperEl = root.querySelector("[data-af-model-wrapper]");
    this.hasWindshieldButtons = root.querySelectorAll("[data-af-has-windshield-btn]");
    this.windshieldButtons = root.querySelectorAll("[data-af-windshield-btn]");
    this.windshieldMessageEl = root.querySelector("[data-af-windshield-message]");
    this.sideviewMirrorCardsContainer = root.querySelector("[data-af-sideview-mirror-cards]");
    this.sideviewMirrorTypeCardsContainer = root.querySelector("[data-af-sideview-mirror-type-cards]");
    this.crossbarCardsContainer = root.querySelector("[data-af-crossbar-cards]");
    this.needDomeLightButtons = root.querySelectorAll("[data-af-need-dome-light-btn]");
    this.needSideviewButtons = root.querySelectorAll("[data-af-need-sideview-btn]");
    this.resultEl = root.querySelector("[data-af-result]");
    this.domePriceEl = root.querySelector("[data-af-dome-price]");

    this.needDomeLight = null;
    this.selectedCrossbarType = null;
    this.needSideview = null;
    this.hasWindshield = null;
    this.selectedWindshieldType = null;
    this.selectedSideviewMirrorAdapter = null; // mirror product: clearview | elite-series-1 | elite-series-2
    this.selectedCageType = null; // cage type: round | profile | bolt-on

    this.rearviewMirrors = null;
    this.rearviewTypeFromProduct = null; // inferred from currentProductHandle for dome lookup
    this.sideviewMirrorAdapters = null; // from sideview-mirror-options.json (3 products)
    this.cageTypeAdapters = null; // from adapter-data sideview-mirror-adapters (round, profile, bolt-on, cab-system)
    this.crossbarAdapters = null;
    this.fitmentIndex = null;
    this.productCache = new Map();
    this.sideviewMirrorCards = []; // cage type cards (Round, Profile, Bolt-On)

    this.plusIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-plus.svg?v=1770403678" alt="" class="af-plus-icon">`;
    this.warningIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-warn.svg?v=1770403679" alt="" class="adapter-finder__warning-icon">`;
    this.checkmarkIcon = `<img src="https://cdn.shopify.com/s/files/1/0400/2841/files/icon-check.svg?v=1770403678" alt="" class="af-checkmark-icon">`;

    this.init();
  }

  async init() {
    if (!this.resultEl) return;

    this.bindEvents();

    if (this.yearEl && this.makeEl) {
      this.populateYears({ startYear: 1990, endYear: new Date().getFullYear() });
      this.disableMakeAndModel();
    }

    try {
      if (this.fitmentUrl) {
        const rawFitment = await this.fetchJson(this.fitmentUrl);
        this.fitmentIndex = this.buildFitmentIndex(rawFitment);
        if (this.makeEl) this.populateMakes();
      }
      if (this.adapterDataUrl) {
        await this.loadAdapterData();
      }
      if (this.sideviewOptionsUrl) {
        await this.loadSideviewOptions();
      }

      if (this.sideviewMirrorAdapters && this.sideviewMirrorTypeCardsContainer) {
        await this.renderSideviewMirrorTypeCards();
      }
      if (this.cageTypeAdapters && this.sideviewMirrorCardsContainer) {
        await this.renderCageTypeCards();
        this.sideviewMirrorCards = this.sideviewMirrorCardsContainer.querySelectorAll("[data-af-sideview-mirror-card]");
      }
      if (this.crossbarAdapters && this.crossbarCardsContainer) {
        await this.renderCrossbarCards();
      }
    } catch (e) {
      // Keep form usable; ensureCrossbarCardsRendered() will load data when crossbar step is shown
    }
    this.updateQuestionNumbers();
  }

  async loadAdapterData() {
    if (!this.adapterDataUrl) return false;
    try {
      const data = await this.fetchJson(this.adapterDataUrl);
      this.rearviewMirrors = this.rearviewMirrors || data?.["rearview-mirrors"] || null;
      this.cageTypeAdapters = this.cageTypeAdapters || data?.["sideview-mirror-adapters"] || null;
      if (!this.sideviewOptionsUrl) {
        this.sideviewMirrorAdapters = this.sideviewMirrorAdapters || data?.["sideview-mirror-adapters"] || null;
      }
      this.crossbarAdapters = this.crossbarAdapters || data?.["crossbar-adapters"] || null;
      if (this.rearviewMirrors && this.currentProductHandle && !this.rearviewTypeFromProduct) {
        for (const [key, config] of Object.entries(this.rearviewMirrors)) {
          if (config?.handle === this.currentProductHandle) {
            this.rearviewTypeFromProduct = key;
            break;
          }
        }
      }
      this.updateDomePriceDisplay();
      return true;
    } catch (_) {
      return false;
    }
  }

  async loadSideviewOptions() {
    if (!this.sideviewOptionsUrl) return false;
    try {
      const data = await this.fetchJson(this.sideviewOptionsUrl);
      if (Array.isArray(data)) {
        this.sideviewMirrorAdapters = {};
        data.forEach((item) => {
          const id = item.id || item.key || item.handle;
          if (id) {
            this.sideviewMirrorAdapters[id] = {
              label: item.label || id,
              handle: item.handle,
              variant_id: item.variant_id,
              sku: item.sku,
              polarisOnly: item.polarisOnly,
              price: item.price
            };
          }
        });
      } else if (data && typeof data === "object") {
        this.sideviewMirrorAdapters = { ...data };
      }
      // Leave out mirrors hidden in Theme settings > Product grid > Hidden SKUs
      const hiddenSkus = (window.theme && window.theme.hiddenSkus) || [];
      if (this.sideviewMirrorAdapters) {
        Object.keys(this.sideviewMirrorAdapters).forEach((key) => {
          const sku = this.sideviewMirrorAdapters[key]?.sku;
          if (sku && hiddenSkus.includes(sku)) delete this.sideviewMirrorAdapters[key];
        });
      }
      return !!this.sideviewMirrorAdapters;
    } catch (_) {
      return false;
    }
  }

  buildFitmentIndex(raw) {
    const index = new Map();
    for (const makeLabel of Object.keys(raw || {})) {
      const makeKey = this.normalizeKey(makeLabel);
      const modelsRaw = raw[makeLabel] || {};
      const modelMap = new Map();
      for (const modelLabel of Object.keys(modelsRaw)) {
        const modelKey = this.normalizeKey(modelLabel);
        const rules = Array.isArray(modelsRaw[modelLabel]) ? modelsRaw[modelLabel] : [];
        modelMap.set(modelKey, { label: modelLabel, rules });
      }
      index.set(makeKey, { label: makeLabel, models: modelMap });
    }
    return index;
  }

  bindEvents() {
    if (this.yearEl) {
      this.yearEl.addEventListener("change", () => {
        if (!this.yearEl.value) {
          if (this.makeEl) this.makeEl.value = "";
          this.clearModels();
          this.disableMakeAndModel();
          this.clearResults();
          return;
        }
        this.makeEl.disabled = false;
        this.modelEl.value = "";
        this.modelEl.disabled = true;
        this.clearResults();
        if (this.makeEl.value) this.populateModelsForMake();
      });
    }
    if (this.makeEl) {
      this.makeEl.addEventListener("change", () => {
        this.modelEl.value = "";
        this.clearResults();
        if (this.modelWrapperEl) this.modelWrapperEl.style.display = "grid";
        this.populateModelsForMake();
        this.updateHasWindshieldButtons(false);
        this.updateSideviewMirrorTypeStep(false);
        this.updateSideviewMirrorStep(false);
      });
    }
    if (this.modelEl) {
      this.modelEl.addEventListener("change", async () => {
        this.clearResults();
        if (this.modelEl.value && this.yearEl.value && this.makeEl.value) {
          const matchResult = this.findAdapterMatch({
            year: this.yearEl.value,
            makeKey: this.makeEl.value,
            modelKey: this.modelEl.value
          });
          if (matchResult.match) {
            this.updateSideviewMirrorTypeStep(true);
            this.updateBoltOnButton();
            if (this.canResolve()) this.resolve();
          } else {
            this.updateHasWindshieldButtons(false);
            this.updateSideviewMirrorTypeStep(false);
            this.updateSideviewMirrorStep(false);
            if (matchResult.yearRange) {
              const makeLabel = this.fitmentIndex.get(this.makeEl.value)?.label || this.makeEl.value;
              const model = this.fitmentIndex.get(this.makeEl.value)?.models.get(this.modelEl.value);
              await this.renderNoMatchWithYearRange(makeLabel, model?.label || this.modelEl.value, matchResult.yearRange);
            } else {
              this.renderNoMatch();
            }
          }
        }
      });
    }

    this.hasWindshieldButtons.forEach(btn => {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectHasWindshield(btn.getAttribute("data-value") === "yes");
        this.clearResults();
        if (this.hasWindshield) this.updateWindshieldStep(true);
        else {
          this.updateWindshieldStep(false);
          if (this.yearEl?.value && this.makeEl?.value && this.modelEl?.value) this.updateSideviewMirrorStep(true);
        }
        if (this.canResolve()) this.resolve();
      };
      btn.addEventListener("click", handler);
      btn.addEventListener("touchend", handler);
    });

    this.windshieldButtons.forEach(btn => {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectWindshieldType(btn.getAttribute("data-value"));
        this.clearResults();
        if (this.yearEl?.value && this.makeEl?.value && this.modelEl?.value) this.updateSideviewMirrorStep(true);
        if (this.canResolve()) this.resolve();
      };
      btn.addEventListener("click", handler);
      btn.addEventListener("touchend", handler);
    });

    this.needDomeLightButtons.forEach(btn => {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.needDomeLight = value === "yes";
        this.setButtonGroupActive(this.needDomeLightButtons, value);
        this.clearResults();
        const crossbarField = this.crossbarCardsContainer?.closest(".adapter-finder__field--mounting-kit-crossbar");
        if (crossbarField) {
          crossbarField.style.display = "grid";
          this.ensureCrossbarCardsRendered();
        }
        const needSideviewField = this.root.querySelector(".adapter-finder__field--need-sideview");
        if (needSideviewField) needSideviewField.style.display = "grid";
        this.updateQuestionNumbers();
        if (this.canResolve()) this.resolve();
      };
      btn.addEventListener("click", handler);
      btn.addEventListener("touchend", handler);
    });

    this.needSideviewButtons.forEach(btn => {
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const value = btn.getAttribute("data-value");
        this.needSideview = value === "yes";
        this.setButtonGroupActive(this.needSideviewButtons, value);
        this.clearResults();
        if (this.needSideview) {
          this.showVehicleFields(true);
          this.renderMessage("Select year, make, and model to add side mirrors.");
        } else {
          this.showVehicleFields(false);
          if (this.canResolve()) this.resolve();
        }
      };
      btn.addEventListener("click", handler);
      btn.addEventListener("touchend", handler);
    });
  }

  setButtonGroupActive(buttons, selectedValue) {
    if (!buttons || !buttons.length) return;
    buttons.forEach(btn => {
      btn.classList.toggle("active", btn.getAttribute("data-value") === selectedValue);
    });
  }

  showVehicleFields(show) {
    const yearField = this.yearEl?.closest(".adapter-finder__field");
    const makeField = this.makeEl?.closest(".adapter-finder__field");
    if (yearField) yearField.style.display = show ? "grid" : "none";
    if (makeField) makeField.style.display = show ? "grid" : "none";
    if (this.modelWrapperEl) this.modelWrapperEl.style.display = show ? "grid" : "none";
    /* Windshield and sideview steps only show after year/make/model and mirror type are selected */
    const hasWindshieldField = this.hasWindshieldButtons[0]?.closest(".adapter-finder__field--has-windshield");
    if (hasWindshieldField) hasWindshieldField.style.display = "none";
    const windshieldField = this.windshieldButtons[0]?.closest(".adapter-finder__field--windshield");
    if (windshieldField) windshieldField.style.display = "none";
    const sideviewMirrorTypeField = this.sideviewMirrorTypeCardsContainer?.closest(".adapter-finder__field--sideview-mirror-type");
    if (sideviewMirrorTypeField) sideviewMirrorTypeField.style.display = "none";
    const sideviewField = this.sideviewMirrorCardsContainer?.closest(".adapter-finder__field--sideview-mirror");
    if (sideviewField) sideviewField.style.display = "none";
    if (this.windshieldMessageEl) this.windshieldMessageEl.style.display = "none";
    if (show && this.makeEl) this.makeEl.disabled = false;
    if (!show) {
      this.updateHasWindshieldButtons(false);
      this.updateSideviewMirrorTypeStep(false);
      this.updateWindshieldStep(false);
      this.updateSideviewMirrorStep(false);
    }
    this.updateQuestionNumbers();
  }

  updateQuestionNumbers() {
    let questionNum = 1;
    const grid = this.root.querySelector(".adapter-finder__grid");
    if (!grid) return;
    grid.querySelectorAll(".adapter-finder__field").forEach(field => {
      if (field.style.display === "none") return;
      const label = field.querySelector("[data-af-label]");
      if (!label) return;
      const baseText = (label.textContent || "").replace(/^\d+\.\s*/, "").trim();
      if (!baseText) return;
      label.textContent = `${questionNum}. ${baseText}`;
      questionNum++;
    });
  }

  updateSideviewMirrorTypeStep(show) {
    const field = this.sideviewMirrorTypeCardsContainer?.closest(".adapter-finder__field--sideview-mirror-type");
    if (field) field.style.display = show ? "grid" : "none";
    if (!show) {
      this.selectedSideviewMirrorAdapter = null;
      this.setSideviewMirrorTypeCardActive(null);
      this.updateHasWindshieldButtons(false);
    }
    this.updateQuestionNumbers();
  }

  updateHasWindshieldButtons(show) {
    const field = this.hasWindshieldButtons[0]?.closest(".adapter-finder__field--has-windshield");
    if (field) field.style.display = show ? "grid" : "none";
    if (!show) {
      this.hasWindshield = null;
      this.hasWindshieldButtons.forEach(b => b.classList.remove("active"));
      this.updateWindshieldStep(false);
      this.updateSideviewMirrorStep(false);
    }
    this.updateQuestionNumbers();
  }

  updateWindshieldStep(show) {
    const field = this.windshieldButtons[0]?.closest(".adapter-finder__field--windshield");
    if (field) field.style.display = show ? "grid" : "none";
    if (!show) {
      this.selectedWindshieldType = null;
      this.windshieldButtons.forEach(b => b.classList.remove("active"));
      this.updateWindshieldMessage(false);
    }
    this.updateQuestionNumbers();
  }

  updateWindshieldMessage(show) {
    if (!this.windshieldMessageEl) return;
    if (show) {
      this.windshieldMessageEl.innerHTML = `<div class="adapter-finder__windshield-message-content">${this.warningIcon}<span>FIXED WINDSHIELDS - NEED 1/8" CLEARANCE TO WORK</span></div>`;
      this.windshieldMessageEl.style.display = "block";
    } else {
      this.windshieldMessageEl.style.display = "none";
      this.windshieldMessageEl.innerHTML = "";
    }
  }

  updateSideviewMirrorStep(show) {
    const field = this.sideviewMirrorCardsContainer?.closest(".adapter-finder__field--sideview-mirror");
    if (field) field.style.display = show ? "grid" : "none";
    if (!show) {
      this.selectedCageType = null;
      this.setSideviewMirrorCardActive(null);
    } else {
      this.updateBoltOnButton();
    }
    this.updateQuestionNumbers();
  }

  selectCageType(value) {
    this.selectedCageType = value;
    this.setSideviewMirrorCardActive(value);
  }

  setSideviewMirrorTypeCardActive(selectedValue) {
    if (!this.sideviewMirrorTypeCardsContainer) return;
    this.sideviewMirrorTypeCardsContainer.querySelectorAll("[data-af-sideview-mirror-type-card]").forEach(card => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  selectHasWindshield(value) {
    this.hasWindshield = value;
    this.setButtonGroupActive(this.hasWindshieldButtons, value ? "yes" : "no");
  }

  selectWindshieldType(value) {
    this.selectedWindshieldType = value;
    this.setButtonGroupActive(this.windshieldButtons, value);
    this.updateWindshieldMessage(value === "fixed");
  }

  selectSideviewMirrorAdapter(value) {
    this.selectedSideviewMirrorAdapter = value;
    this.setSideviewMirrorTypeCardActive(value);
  }

  setSideviewMirrorCardActive(selectedValue) {
    if (!this.sideviewMirrorCardsContainer) return;
    this.sideviewMirrorCardsContainer.querySelectorAll("[data-af-sideview-mirror-card]").forEach(card => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  setCrossbarCardActive(selectedValue) {
    if (!this.crossbarCardsContainer) return;
    this.crossbarCardsContainer.querySelectorAll("[data-af-mounting-kit-crossbar-card]").forEach(card => {
      card.classList.toggle("is-active", card.getAttribute("data-value") === selectedValue);
    });
  }

  async updateDomePriceDisplay() {
    if (!this.domePriceEl) return;
    const config = this.rearviewTypeFromProduct && this.rearviewMirrors?.[this.rearviewTypeFromProduct];
    const domeHandle = config?.dome?.handle;
    if (!domeHandle) {
      this.domePriceEl.textContent = "- ";
      return;
    }
    try {
      const product = await this.fetchProductByHandle(domeHandle);
      const priceCents = product?.variants?.[0]?.price;
      const formatted = priceCents != null ? this.moneyFromCents(priceCents) : "";
      this.domePriceEl.textContent = formatted ? `- ${formatted}` : "- ";
    } catch (_) {
      this.domePriceEl.textContent = "- ";
    }
  }

  createCardElement(label, img, dataAttr, dataValue, priceFormatted = null) {
    const card = document.createElement("div");
    card.className = "adapter-finder__card";
    card.setAttribute(dataAttr, "");
    card.setAttribute("data-value", dataValue);
    const priceHtml = priceFormatted ? `<span class="adapter-finder__card__price">${this.escapeHtml(priceFormatted)}</span>` : "";
    card.innerHTML = `
      ${img ? `<img class="adapter-finder__card__img" src="${img}" alt="${this.escapeHtml(label)}">` : `<div class="adapter-finder__card__img"></div>`}
      <span class="adapter-finder__card__label">${this.escapeHtml(label)}</span>
      ${priceHtml}
    `;
    return card;
  }

  async ensureCrossbarCardsRendered() {
    if (!this.crossbarCardsContainer) return;
    if (this.crossbarCardsContainer.querySelector("[data-af-mounting-kit-crossbar-card]")) return;
    if (!this.crossbarAdapters && this.adapterDataUrl) {
      await this.loadAdapterData();
    }
    if (this.crossbarAdapters && Object.keys(this.crossbarAdapters).length) {
      await this.renderCrossbarCards();
    }
  }

  async renderSideviewMirrorTypeCards() {
    if (!this.sideviewMirrorTypeCardsContainer || !this.sideviewMirrorAdapters) return;
    this.sideviewMirrorTypeCardsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.sideviewMirrorAdapters)) {
      const label = config?.label || key;
      const handle = config?.handle;
      let img = "";
      let priceFormatted = null;
      if (handle) {
        const product = await this.fetchProductByHandle(handle);
        // Multiple keys can share one handle (e.g. Elite Series 1/2 are now
        // variants of the same merged product) — resolve by SKU so each
        // card shows its own price/image instead of always variants[0]'s.
        const variant = config?.sku ? this.findVariantBySku(product, config.sku) : product?.variants?.[0];
        img = variant?.featured_image?.src || product?.featured_image || product?.images?.[0] || "";
        const priceCents = variant?.price;
        if (priceCents != null) priceFormatted = this.moneyFromCents(priceCents);
      } else {
        priceFormatted = config?.price || null;
      }
      const card = this.createCardElement(label, img, "data-af-sideview-mirror-type-card", key, priceFormatted);
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectSideviewMirrorAdapter(key);
        this.clearResults();
        this.updateHasWindshieldButtons(true);
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handler);
      card.addEventListener("touchend", handler);
      this.sideviewMirrorTypeCardsContainer.appendChild(card);
    }
  }

  async renderCageTypeCards() {
    if (!this.sideviewMirrorCardsContainer || !this.cageTypeAdapters) return;
    this.sideviewMirrorCardsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.cageTypeAdapters)) {
      const label = config?.label || config?.title || key;
      const handle = config?.handle;
      const polarisOnly = config?.polarisOnly === true;
      const canAmMaverickX3Only = config?.canAmMaverickX3Only === true;
      let img = "";
      let priceFormatted = null;
      if (handle) {
        const product = await this.fetchProductByHandle(handle);
        img = product?.featured_image || product?.images?.[0] || "";
        const priceCents = product?.variants?.[0]?.price;
        if (priceCents != null) priceFormatted = this.moneyFromCents(priceCents);
      } else {
        priceFormatted = config?.price || null;
      }
      const card = this.createCardElement(label, img, "data-af-sideview-mirror-card", key, priceFormatted);
      if (polarisOnly || canAmMaverickX3Only) card.style.display = "none";
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectCageType(key);
        this.clearResults();
        if (key === "cab-system") {
          this.renderComingSoonSignup();
          return;
        }
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handler);
      card.addEventListener("touchend", handler);
      this.sideviewMirrorCardsContainer.appendChild(card);
    }
    this.updateBoltOnButton();
  }

  async renderCrossbarCards() {
    if (!this.crossbarCardsContainer || !this.crossbarAdapters) return;
    this.crossbarCardsContainer.innerHTML = "";
    for (const [key, config] of Object.entries(this.crossbarAdapters)) {
      const label = config?.title || key;
      const handle = config?.handle;
      let img = "";
      let priceFormatted = null;
      if (handle) {
        const product = await this.fetchProductByHandle(handle);
        img = product?.featured_image || product?.images?.[0] || "";
        const priceCents = product?.variants?.[0]?.price;
        if (priceCents != null) priceFormatted = this.moneyFromCents(priceCents);
      }
      const card = this.createCardElement(label, img, "data-af-mounting-kit-crossbar-card", key, priceFormatted);
      const handler = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.selectedCrossbarType = key;
        this.setCrossbarCardActive(key);
        this.clearResults();
        const needSideviewField = this.root.querySelector(".adapter-finder__field--need-sideview");
        if (needSideviewField) needSideviewField.style.display = "grid";
        if (this.canResolve()) this.resolve();
      };
      card.addEventListener("click", handler);
      card.addEventListener("touchend", handler);
      this.crossbarCardsContainer.appendChild(card);
    }
  }

  updateBoltOnButton() {
    const makeKey = this.normalizeKey(this.makeEl?.value);
    const modelKey = this.normalizeKey(this.modelEl?.value);
    const showBoltOn = makeKey === "POLARIS" && (modelKey === "RZR" || modelKey === "GENERAL" || modelKey === "RANGER");
    const showCabSystem = makeKey === "POLARIS" && modelKey !== "RZR";
    const showMaverickX3Nor = makeKey === "CANAM" && (modelKey === "MAVERICK X3" || modelKey.startsWith("MAVERICK X3 "));

    if (this.selectedCageType === "bolt-on" && !showBoltOn) this.selectCageType(null);
    if (this.selectedCageType === "maverick-x3-nor" && !showMaverickX3Nor) this.selectCageType(null);

    this.sideviewMirrorCards.forEach(card => {
      const value = card.getAttribute("data-value");
      if (value === "bolt-on") card.style.display = showBoltOn ? "block" : "none";
      else if (value === "cab-system") card.style.display = showCabSystem ? "block" : "none";
      else if (value === "maverick-x3-nor") card.style.display = showMaverickX3Nor ? "block" : "none";
      else card.style.display = "block";
    });
    if (this.sideviewMirrorCardsContainer) {
      this.sideviewMirrorCardsContainer.classList.toggle("adapter-finder__cards--four-cols", showCabSystem);
    }
  }

  populateYears(opts) {
    const { startYear, endYear } = opts || {};
    for (let y = endYear; y >= startYear; y--) {
      this.yearEl.appendChild(this.option(String(y), String(y)));
    }
  }

  populateMakes() {
    const makes = Array.from(this.fitmentIndex.values()).map(m => m.label);
    const items = this.sortUnique(makes).map(label => ({ label, value: this.normalizeKey(label) }));
    items.sort((a, b) => {
      if (a.value === "OTHER" && b.value !== "OTHER") return 1;
      if (b.value === "OTHER" && a.value !== "OTHER") return -1;
      return (a.label || "").localeCompare(b.label || "");
    });
    this.setSelectOptions(this.makeEl, items, "Select make");
  }

  populateModelsForMake() {
    this.clearModels();
    const makeKey = this.makeEl.value;
    const make = this.fitmentIndex.get(makeKey);
    if (!make) return;
    const models = Array.from(make.models.values()).map(m => m.label);
    const items = this.sortUnique(models).map(m => ({
      label: makeKey === "OTHER" && m === "MODEL" ? "Any" : m,
      value: this.normalizeKey(m)
    }));
    this.setSelectOptions(this.modelEl, items, "Select model");
    this.modelEl.disabled = false;
  }

  setSelectOptions(select, items, placeholder) {
    if (!select) return;
    select.innerHTML = "";
    select.appendChild(this.option("", placeholder));
    items.forEach(item => select.appendChild(this.option(item.value, item.label)));
  }

  option(value, label) {
    const opt = document.createElement("option");
    opt.value = value;
    opt.textContent = label;
    return opt;
  }

  disableMakeAndModel() {
    if (this.makeEl) this.makeEl.disabled = true;
    if (this.modelEl) this.modelEl.disabled = true;
  }

  clearModels() {
    this.setSelectOptions(this.modelEl, [], "Select model");
    if (this.modelEl) this.modelEl.disabled = true;
  }

  clearResults() {
    if (this.resultEl) this.resultEl.innerHTML = "";
  }

  canResolve() {
    if (this.needDomeLight === null) return false;

    const crossbarField = this.crossbarCardsContainer?.closest(".adapter-finder__field--mounting-kit-crossbar");
    const crossbarVisible = crossbarField && crossbarField.style.display !== "none" && crossbarField.style.display !== "";
    if (crossbarVisible && this.crossbarAdapters && Object.keys(this.crossbarAdapters).length) {
      if (!this.selectedCrossbarType) return false;
    }

    const needSideviewField = this.root.querySelector(".adapter-finder__field--need-sideview");
    const needSideviewVisible = needSideviewField && needSideviewField.style.display !== "none";
    if (needSideviewVisible && this.needSideview === null) return false;

    if (this.needSideview === false) return true;
    if (this.needSideview !== true) return false;

    const year = this.yearEl?.value;
    const makeKey = this.makeEl?.value;
    const modelKey = this.modelEl?.value;
    if (!year || !makeKey || !modelKey) return false;
    const matchResult = this.findAdapterMatch({ year, makeKey, modelKey });
    if (!matchResult || !matchResult.match) return false;
    if (!this.selectedSideviewMirrorAdapter) return false;
    if (this.hasWindshield === null) return false;
    if (this.hasWindshield && !this.selectedWindshieldType) return false;
    if (!this.selectedCageType) return false;
    if (this.selectedCageType === "cab-system") return false;
    return true;
  }

  async resolve() {
    if (!this.canResolve()) return;

    const year = this.yearEl?.value;
    const makeKey = this.makeEl?.value;
    const modelKey = this.modelEl?.value;
    const noVehicle = this.needSideview === false;

    if (!noVehicle && this.needSideview === true) {
      const matchResult = this.findAdapterMatch({ year, makeKey, modelKey });
      if (!matchResult?.match) {
        if (matchResult?.yearRange) {
          const makeLabel = this.fitmentIndex.get(makeKey)?.label || makeKey;
          const model = this.fitmentIndex.get(makeKey)?.models.get(modelKey);
          await this.renderNoMatchWithYearRange(makeLabel, model?.label || modelKey, matchResult.yearRange);
        } else {
          this.renderNoMatch();
        }
        return;
      }
    }

    this.renderLoading();

    if (this.needSideview === true && (this.selectedSideviewMirrorAdapter || this.selectedCageType) && (!this.sideviewMirrorAdapters || !this.cageTypeAdapters)) {
      await this.loadAdapterData();
      if (this.sideviewOptionsUrl) await this.loadSideviewOptions();
    }

    const rearviewConfig = this.rearviewTypeFromProduct && this.rearviewMirrors?.[this.rearviewTypeFromProduct];
    const mainHandle = this.needDomeLight && rearviewConfig?.dome?.handle
      ? rearviewConfig.dome.handle
      : (rearviewConfig?.handle || this.currentProductHandle);

    const productHandles = new Set();
    if (mainHandle) productHandles.add(mainHandle);
    if (this.selectedCrossbarType && this.crossbarAdapters?.[this.selectedCrossbarType]?.handle) {
      const h = this.crossbarAdapters[this.selectedCrossbarType].handle;
      if (h) productHandles.add(h);
    }

    const adapterConfigs = [];
    if (this.needSideview === true && makeKey && modelKey && this.selectedSideviewMirrorAdapter && this.selectedSideviewMirrorAdapter !== "cab-system" && this.selectedCageType && this.selectedCageType !== "cab-system") {
      const mirrorConfig = this.sideviewMirrorAdapters?.[this.selectedSideviewMirrorAdapter];
      const mirrorHandle = mirrorConfig?.handle;
      if (mirrorHandle) {
        productHandles.add(mirrorHandle);
        adapterConfigs.push({
          handle: mirrorHandle,
          variant_id: mirrorConfig?.variant_id ?? null,
          sku: mirrorConfig?.sku ?? null
        });
      }
      const cageInfo = this.getProductHandleAndSku(makeKey, modelKey, this.selectedCageType);
      if (cageInfo?.handle) {
        productHandles.add(cageInfo.handle);
        adapterConfigs.push({
          handle: cageInfo.handle,
          variant_id: null,
          sku: cageInfo.sku ?? null
        });
      }
    }

    const bundleProducts = [];
    for (const handle of productHandles) {
      const product = await this.fetchProductByHandle(handle);
      if (product) bundleProducts.push(product);
    }

    const productInfo = adapterConfigs.length > 0 ? { adapterConfigs } : null;
    const displayHandles = Array.from(productHandles);

    const makeLabel = noVehicle ? "" : (this.fitmentIndex.get(makeKey)?.label || makeKey);
    const modelLabel = noVehicle ? "" : (this.fitmentIndex.get(makeKey)?.models.get(modelKey)?.label || modelKey);

    this.renderProducts(bundleProducts, {
      year: year || "",
      make: makeLabel,
      model: modelLabel,
      adapterConfigs,
      displayHandles,
      noVehicle
    });
  }

  findAdapterMatch({ year, makeKey, modelKey }) {
    const make = this.fitmentIndex?.get(makeKey);
    if (!make) return { match: false };
    const model = make.models.get(modelKey);
    if (!model) return { match: false };
    const y = Number(year);
    if (!Number.isFinite(y)) return { match: false };
    let minYear = Infinity, maxYear = -Infinity, hasMatch = false;
    for (const rule of model.rules) {
      const from = Number(rule?.from), to = Number(rule?.to);
      if (Number.isFinite(from) && Number.isFinite(to)) {
        minYear = Math.min(minYear, from);
        maxYear = Math.max(maxYear, to);
        if (y >= from && y <= to) hasMatch = true;
      }
    }
    if (makeKey === "OTHER") return { match: true };
    if (hasMatch) return { match: true };
    if (Number.isFinite(minYear) && Number.isFinite(maxYear)) {
      return { match: false, yearRange: { from: minYear, to: maxYear } };
    }
    return { match: false };
  }

  getProductHandleAndSku(makeKey, modelKey, cageType) {
    const mk = this.normalizeKey(makeKey);
    const mod = this.normalizeKey(modelKey);
    if (cageType === "maverick-x3-nor") {
      const cfg = this.cageTypeAdapters?.[cageType];
      if (!cfg?.handle) return null;
      if (mk !== "CANAM" || !(mod === "MAVERICK X3" || mod.startsWith("MAVERICK X3 "))) return null;
      return { handle: cfg.handle, sku: cfg.sku ?? null };
    }
    if (cageType === "round") return { handle: "sideview-round-kit-svm", sku: "ROUND-KIT-SVM" };
    if (cageType === "profile") return { handle: "sideview-profile-kit-svm", sku: "PROFILE-KIT-SVM" };
    if (mk === "POLARIS" && cageType === "bolt-on") {
      return { handle: "sideview-bolt-on-pmirkit-1", sku: "PMIRKIT-1" };
    }
    return null;
  }

  findVariantBySku(product, sku) {
    if (!product?.variants) return product?.variants?.[0] || null;
    if (sku && product.variants.length) {
      const skuUpper = String(sku).toUpperCase().trim();
      const v = product.variants.find(x => String(x.sku || "").toUpperCase().trim() === skuUpper);
      if (v) return v;
    }
    return product?.variants?.[0] || null;
  }

  findVariantById(product, variantId) {
    if (!product?.variants?.length || variantId == null) return product?.variants?.[0] || null;
    const id = Number(variantId);
    if (!Number.isFinite(id)) return product.variants[0];
    const v = product.variants.find(x => Number(x.id) === id);
    return v || product.variants[0];
  }

  async fetchJson(url) {
    const res = await fetch(url, { credentials: "same-origin" });
    if (!res.ok) throw new Error("Failed to load JSON");
    return res.json();
  }

  async fetchProductByHandle(handle) {
    if (this.productCache.has(handle)) return this.productCache.get(handle);
    const res = await fetch(`/products/${handle}.js`, { credentials: "same-origin" });
    if (!res.ok) return null;
    const data = await res.json();
    this.productCache.set(handle, data);
    return data;
  }

  async addMultipleToCart(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items })
    });
    if (!res.ok) throw new Error("Add to cart failed");
    return res.json();
  }

  renderProducts(products, { year, make, model, adapterConfigs, displayHandles, noVehicle }) {
    const showHandles = displayHandles?.length ? new Set(displayHandles) : new Set(products.map(p => p.handle));
    const displayProducts = products.filter(p => showHandles.has(p.handle));

    const cartItems = [];
    let totalPrice = 0;
    for (const product of products) {
      let variant = null;
      const config = Array.isArray(adapterConfigs) && adapterConfigs.length
        ? adapterConfigs.find(c => c && c.handle === product.handle)
        : null;
      if (config) {
        if (config.variant_id != null) variant = this.findVariantById(product, config.variant_id);
        if (!variant && config.sku) variant = this.findVariantBySku(product, config.sku);
      }
      if (!variant && product?.variants?.length) variant = product.variants[0];
      if (variant?.id) {
        cartItems.push({ id: variant.id, quantity: 1 });
        totalPrice += Number(variant.price ?? variant.price_cents ?? 0);
      }
    }
    const totalPriceFormatted = this.moneyFromCents(totalPrice);

    const productsSection = displayProducts.map((product, i) => {
      // Same handle can now represent more than one config (Elite Series
      // 1/2 share a merged product) — use the matching variant's own image
      // when there is one, so the confirmation card doesn't always show
      // variants[0]'s photo regardless of which one was actually selected.
      const config = Array.isArray(adapterConfigs) && adapterConfigs.length
        ? adapterConfigs.find(c => c && c.handle === product.handle)
        : null;
      const displayVariant = config?.sku ? this.findVariantBySku(product, config.sku) : null;
      const img = displayVariant?.featured_image?.src || product?.featured_image || product?.images?.[0] || "";
      const title = product?.title || "Product";
      const productHtml = `<div class="af-product">${img ? `<img class="af-product__img" src="${img}" alt="${this.escapeHtml(title)}">` : `<div class="af-product__img"></div>`}<p class="af-product__title"><span>${this.escapeHtml(title)}</span></p></div>`;
      return i < displayProducts.length - 1 ? productHtml + `<div class="af-plus">${this.plusIcon}</div>` : productHtml;
    }).join("");

    const productText = displayProducts.length === 1 ? "This product" : "These products";
    const needText = displayProducts.length === 1 ? "You will need this product" : "You will need all of the above products";
    const confirmationLine = noVehicle
      ? "You have selected a mirror and crossbar mounting kit."
      : (make === "OTHER"
          ? `${productText} may fit your <strong>${year} ${make} ${model}</strong>. Please contact customer service to confirm fitment. ${needText}.`
          : `${productText} will fit your <strong>${year} ${make} ${model}</strong>! ${needText}.`);

    this.resultEl.innerHTML = `
      <div class="af-products">${productsSection}</div>
      <div class="af-confirmation">
        <div class="af-confirmation__icon">${this.checkmarkIcon}</div>
        <div class="af-confirmation__text">${confirmationLine}</div>
      </div>
      ${cartItems.length > 0 ? `<div class="af-cart"><button type="button" class="af-cart__btn" data-af-add-all>Add To Cart - ${totalPriceFormatted}</button><div class="af-cart__status" data-af-cart-status></div></div>` : ""}
    `;

    const addToCartBtn = this.resultEl.querySelector("[data-af-add-all]");
    const cartStatus = this.resultEl.querySelector("[data-af-cart-status]");
    if (addToCartBtn && cartItems.length > 0) {
      const handler = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (addToCartBtn.disabled) return;
        addToCartBtn.disabled = true;
        if (cartStatus) addToCartBtn.textContent = "Adding to cart...";
        try {
          await this.addMultipleToCart(cartItems);
          if (cartStatus) addToCartBtn.textContent = "Added to cart!";
          document.dispatchEvent(new CustomEvent("theme:cart:refresh", { bubbles: true }));
          const cartDrawer = document.querySelector("#cart-drawer, cart-element[data-cart-drawer]");
          if (cartDrawer && typeof cartDrawer.openCartDrawer === "function") cartDrawer.openCartDrawer();
        } catch (err) {
          if (cartStatus) addToCartBtn.textContent = "Could not add to cart. Please try again.";
        } finally {
          addToCartBtn.disabled = false;
          addToCartBtn.textContent = `Add To Cart - ${totalPriceFormatted}`;
        }
      };
      addToCartBtn.addEventListener("click", handler);
      addToCartBtn.addEventListener("touchend", handler);
    }
  }

  renderMessage(message) {
    if (this.resultEl) this.resultEl.innerHTML = `<div class="af-message">${this.escapeHtml(message)}</div>`;
  }

  renderLoading() {
    if (this.resultEl) this.resultEl.innerHTML = `<div class="af-loading">Loading suggestion...</div>`;
  }

  renderComingSoonSignup() {
    if (!this.resultEl) return;
    this.resultEl.innerHTML = `
      <div class="af-coming-soon">
        <div class="af-coming-soon__icon">${this.checkmarkIcon}</div>
        <p class="af-coming-soon__text">Product coming soon! Get notified by entering your email below.</p>
        <form class="af-coming-soon__form" data-af-coming-soon-form>
          <input type="email" name="email" class="af-coming-soon__input" placeholder="Your email" required aria-label="Email address" autocomplete="email">
          <button type="submit" class="af-coming-soon__btn">Sign up</button>
        </form>
        <p class="af-coming-soon__status" data-af-coming-soon-status aria-live="polite"></p>
      </div>
    `;
    const form = this.resultEl.querySelector("[data-af-coming-soon-form]");
    const statusEl = this.resultEl.querySelector("[data-af-coming-soon-status]");
    if (form) {
      form.addEventListener("submit", (e) => {
        e.preventDefault();
        if (!this.klaviyoPublicKey || !this.klaviyoListId) {
          if (statusEl) statusEl.textContent = "Signup is not configured.";
          return;
        }
        const email = form.querySelector('input[name="email"]')?.value?.trim();
        if (!email) return;
        const btn = form.querySelector("button[type=submit]");
        if (btn) btn.disabled = true;
        if (statusEl) statusEl.textContent = "Signing up...";
        this.subscribeToKlaviyoList(this.klaviyoPublicKey, this.klaviyoListId, email)
          .then(() => {
            if (statusEl) statusEl.textContent = "Thanks! We'll notify you when it's available.";
            if (btn) btn.disabled = false;
            form.reset();
          })
          .catch((err) => {
            if (statusEl) statusEl.textContent = err?.message || "Something went wrong.";
            if (btn) btn.disabled = false;
          });
      });
    }
  }

  async subscribeToKlaviyoList(publicKey, listId, email) {
    const url = `https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(publicKey)}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "revision": "2026-01-15", "accept": "application/vnd.api+json", "content-type": "application/vnd.api+json" },
      body: JSON.stringify({
        data: {
          type: "subscription",
          attributes: {
            custom_source: "Adapter Finder - Cab System",
            profile: {
              data: {
                type: "profile",
                attributes: {
                  email: email,
                  subscriptions: { email: { marketing: { consent: "SUBSCRIBED" } } }
                }
              }
            }
          },
          relationships: { list: { data: { type: "list", id: listId } } }
        }
      })
    });
    if (!res.ok) {
      let errDetail = res.statusText;
      try {
        const j = await res.json();
        if (j?.errors?.[0]) errDetail = j.errors[0].detail || j.errors[0].title || errDetail;
      } catch (_) {}
      throw new Error(errDetail);
    }
  }

  renderNoMatch() {
    if (this.resultEl) {
      this.resultEl.innerHTML = `<div class="af-message"><div>No adapter match found. Contact support to see if we can help you find the right product that will fit your machine.</div></div>`;
    }
  }

  renderNoMatchWithYearRange(make, model, yearRange) {
    if (this.resultEl) {
      this.resultEl.innerHTML = `<div class="af-message"><div>Currently, this product only fits <strong>${make} ${model}</strong> machines from <strong>${yearRange.from}-${yearRange.to}</strong>. Please contact support to see if we can help you find the right product that will fit your machine.</div></div>`;
    }
  }

  sortUnique(arr) {
    return Array.from(new Set(arr)).sort((a, b) => (a || "").localeCompare(b || ""));
  }

  normalizeKey(v) {
    return String(v || "").trim().toUpperCase().replace(/\s+/g, " ").replace(/-/g, "");
  }

  moneyFromCents(cents) {
    const n = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, { style: "currency", currency: Shopify.currency.active }).format(n);
    } catch (e) {
      return `$${n.toFixed(2)}`;
    }
  }

  escapeHtml(str) {
    return String(str || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");
  }
}

document.querySelectorAll("adapter-finder-rearview").forEach((root) => {
  new AdapterFinderRearview(root);
});
